import { Box } from '@mui/material';
import React, { useCallback } from 'react';

import { ItemPicker } from '../../../components/_mui';
import { IPickedMskuItem } from '../../../types/api';
import { MarketplaceDetailsStatusEnum } from '../../../types/marketplace';
import { ICampaignItem } from '../../../types/pyg';
import { isDetailsPending, stripMarketplacePrefix } from '../../../util/marketplace';

interface IProps {
  item: ICampaignItem;
  vendor?: string;
  detailsStatus: MarketplaceDetailsStatusEnum;
  isMarketplaceSend: boolean;
  isOutOfStock: boolean;
  isExpanded: boolean;
  selected: IPickedMskuItem | null;
  disabledItems: string[];
  onExpand: React.Dispatch<React.SetStateAction<string>>;
  onSelect: (internalId: string, skuOption: IPickedMskuItem) => void;
  onViewDetails: (skuOption: ICampaignItem, onSelectOption?: () => void) => void;
  onRetry: (productId: string) => void;
  registerHeaderRef: (internalId: string, el: HTMLDivElement | null) => void;
}

export const OptionsListItem = ({
  item,
  vendor,
  detailsStatus,
  isMarketplaceSend,
  isOutOfStock,
  isExpanded,
  selected,
  disabledItems,
  onExpand,
  onSelect,
  onViewDetails,
  onRetry,
  registerHeaderRef,
}: IProps) => {
  const { internalId, id, sku_options } = item;

  const hasSkuOptions = !!sku_options?.length;
  const hasDetailsFailed = detailsStatus === MarketplaceDetailsStatusEnum.Failed;
  const isDetailsLoading = isMarketplaceSend && isDetailsPending(detailsStatus) && !hasSkuOptions;

  const handleHeaderRef = useCallback(
    (el: HTMLDivElement | null) => registerHeaderRef(internalId, el),
    [registerHeaderRef, internalId],
  );

  const handleSelect = useCallback(
    (skuOption: IPickedMskuItem) => onSelect(internalId, skuOption),
    [onSelect, internalId],
  );

  const handleRetry = useCallback(() => onRetry(stripMarketplacePrefix(id)), [onRetry, id]);

  return (
    <Box ref={handleHeaderRef}>
      <ItemPicker
        item={item}
        vendor={vendor}
        isExpandable={hasSkuOptions && !isDetailsLoading && !isOutOfStock}
        expanded={isExpanded}
        onExpand={onExpand}
        onSelect={handleSelect}
        selected={selected}
        onViewDetails={onViewDetails}
        disabledItems={disabledItems}
        isLoading={isDetailsLoading}
        hasFailed={hasDetailsFailed}
        isOutOfStock={isOutOfStock}
        onRetry={hasDetailsFailed ? handleRetry : undefined}
      />
    </Box>
  );
};
